import api, { getApiBase } from "../utils/api";

/**
 * Efficiency Initiatives API client — Tools -> Efficiency Initiatives.
 *
 * Backed by routers/efficiency_initiatives.py in the enviz_api repo. Unlike
 * ticketService.js there is no localStorage fallback: an initiative is the record
 * the savings numbers are computed from, so a row that only exists in one browser
 * would report impact nobody else can see. A failed write surfaces as an error.
 */

const BASE = "/efficiency-initiatives";

// utils/api.js sets a 60s default, which is too tight for anything that runs the
// enSight detectors: the server's own insight-loss budget is 45s and the energy
// pass happens before it, so rank_by=loss was measured at 54.6s on a 4-initiative
// client — inside 60s, but with no margin, and a larger portfolio would abort
// client-side while the server was still working. setupProxy.js already allows
// 300s for exactly this reason ("the enSight detectors legitimately run for tens
// of seconds"). Only the slow calls opt in; the CRUD calls keep the 60s default.
const DETECTOR_TIMEOUT_MS = 180000;

/** Pull the readable message out of the backend's {error:{message,reason}} envelope. */
export function initiativeErrorMessage(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error?.message ||
    err?.response?.data?.detail ||
    err?.parsedError?.message ||
    err?.message ||
    fallback
  );
}

/**
 * Form dropdown options: statuses, node types and the full insight catalogue
 * grouped by category.
 *
 * Fetched rather than hardcoded — the catalogue is derived on the server from the
 * enSight *_META dicts, so a newly added insight appears here with no UI change.
 */
export async function getInitiativeOptions() {
  const { data } = await api.get(`${BASE}/options`);
  return data;
}

/** Building / Section / Machine nodes this user is allowed to target. */
export async function getSelectableNodes(username) {
  const { data } = await api.get(`${BASE}/nodes`, { params: { username } });
  return data.nodes || [];
}

/**
 * List initiatives with the tab's filters applied.
 *
 * Empty strings and "All" are stripped rather than sent: the backend treats a
 * missing param as "no filter", and sending status="" would fail validation.
 */
export async function listInitiatives(username, filters = {}) {
  const params = { username };
  const passthrough = ["status", "node_type", "meter_id", "insight_key", "q", "from", "to"];
  passthrough.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "" && value !== "All") {
      params[key] = value;
    }
  });
  if (filters.offset != null) params.offset = filters.offset;
  if (filters.limit != null) params.limit = filters.limit;

  const { data } = await api.get(BASE, { params });
  return data;
}

/**
 * What the selected insights are costing over a baseline window, annualised.
 *
 * Fills the expected-saving fields on the create form from measured loss instead
 * of leaving the user to invent a target the tab will later score them against.
 *
 * Opts into the long timeout: it wakes the enSight detectors over the window, so
 * it belongs with the other detector-backed calls rather than the 60s CRUD default.
 */
export async function estimateExpectedSavings({
  username,
  meterId,
  insightKeys,
  baselineStart,
  baselineEnd,
}) {
  const params = new URLSearchParams();
  params.append("username", username);
  params.append("meter_id", meterId);
  // Repeated key, matching the List[str] Query on the server. URLSearchParams is
  // used rather than axios' params object because the default serialiser would
  // send insight_key[]= and FastAPI would not bind it.
  (insightKeys || []).forEach((k) => params.append("insight_key", k));
  params.append("baseline_start", baselineStart);
  params.append("baseline_end", baselineEnd);

  const { data } = await api.get(`${BASE}/expected-savings?${params.toString()}`, {
    timeout: DETECTOR_TIMEOUT_MS,
  });
  return data;
}

/** One initiative, including its change history. */
export async function getInitiative(initiativeId, username) {
  const { data } = await api.get(`${BASE}/${initiativeId}`, { params: { username } });
  return data.data;
}

export async function createInitiative(payload) {
  const { data } = await api.post(BASE, payload);
  return data.data;
}

/** Full replace — the edit form submits every field, so a cleared date clears it. */
export async function updateInitiative(initiativeId, payload) {
  const { data } = await api.put(`${BASE}/${initiativeId}`, payload);
  return data.data;
}

/** Status transition. Also the "Complete" row action. */
export async function setInitiativeStatus(initiativeId, payload) {
  const { data } = await api.patch(`${BASE}/${initiativeId}/status`, payload);
  return data.data;
}

/* --------------------------------------------------------------------------
 * Attachments — supporting documents, photographs, invoices
 *
 * Multipart, same shape as uploadMachinePicture in controllers/machineController.js.
 * Entirely optional: the Complete dialog calls this only when the user picked
 * something, and completing without evidence is the normal path.
 * ------------------------------------------------------------------------ */

/** Evidence already attached to an initiative. */
export async function listInitiativeAttachments(initiativeId, username) {
  const { data } = await api.get(`${BASE}/${initiativeId}/attachments`, {
    params: { username },
  });
  return data.attachments || [];
}

/**
 * Attach one or more files. `files` is a FileList or an array of File objects.
 *
 * The browser sets its own multipart boundary, so Content-Type is left to axios
 * rather than being hardcoded — setting it by hand without a boundary makes the
 * server unable to parse the body.
 */
export async function uploadInitiativeAttachments(initiativeId, username, files, note) {
  const form = new FormData();
  form.append("username", username);
  Array.from(files || []).forEach((file) => form.append("files", file));
  if (note) form.append("note", note);

  const { data } = await api.post(`${BASE}/${initiativeId}/attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    // A 10 MB scan over a slow office uplink outlasts the 60s default.
    timeout: DETECTOR_TIMEOUT_MS,
  });
  return data.attachments || [];
}

export async function deleteInitiativeAttachment(initiativeId, attachmentId, username) {
  const { data } = await api.delete(
    `${BASE}/${initiativeId}/attachments/${attachmentId}`,
    { params: { username } }
  );
  return data.attachments || [];
}

/**
 * Download URL for one attachment.
 *
 * A plain href rather than a fetch: the file is streamed with a Content-Disposition
 * filename, so letting the browser handle it gives the user their own filename and
 * their own download UI for free. The API base is prepended by hand because this
 * string leaves axios and goes into the DOM.
 */
export function attachmentDownloadUrl(initiativeId, attachmentId, username) {
  const base = getApiBase() || "";
  return (
    `${base}${BASE}/${initiativeId}/attachments/${attachmentId}/download` +
    `?username=${encodeURIComponent(username)}`
  );
}

/** Soft delete: the row stops appearing but historical totals stay reproducible. */
export async function deleteInitiative(initiativeId, username) {
  const { data } = await api.delete(`${BASE}/${initiativeId}`, { params: { username } });
  return data;
}

/**
 * Measured before/after impact for one initiative: energy AND insight loss.
 *
 * Always recomputed server-side from meter data, so this is a live call rather
 * than something to cache alongside the initiative row. `grain` buckets both
 * trend series: daily | weekly | monthly | yearly.
 *
 * Returns both halves of the response. They are separate top-level blocks
 * because they can fail independently — a node can have clean hourly energy data
 * and still have no measurable insight loss (or vice versa), so one being
 * unavailable must not blank the other.
 *
 * The insight-loss half calls the enSight detectors over both windows and takes
 * roughly ten seconds on a real node. Callers need a loading state; pass
 * insightLoss:false to skip it, and insightTrend:false to keep the headline
 * figures but drop the per-bucket series (the expensive part).
 */
export async function getInitiativeImpact(
  initiativeId,
  username,
  grain = "daily",
  { insightLoss = true, insightTrend = true } = {}
) {
  const { data } = await api.get(`${BASE}/${initiativeId}/impact`, {
    params: {
      username,
      grain,
      include_insight_loss: insightLoss,
      include_insight_trend: insightTrend,
    },
    ...(insightLoss ? { timeout: DETECTOR_TIMEOUT_MS } : {}),
  });
  return { impact: data.impact, insightLoss: data.insight_loss };
}

/**
 * Ranked initiative performance, plus the best and worst outliers.
 *
 * `rankBy`: energy | financial | payback | loss.
 *
 * rank_by="loss" makes the server run the insight-loss pass whether or not
 * includeInsightLoss is set — ranking on a column of nulls would be an arbitrary
 * order presented as a result. Always check `ranking_available` before drawing
 * one: on a large client the pass can time out with nothing measured, and the
 * response says so rather than inventing an order.
 */
export async function getInitiativePerformance(
  username,
  { rankBy = "energy", status, top = 5, includeInsightLoss = false } = {}
) {
  const params = { username, rank_by: rankBy, top };
  if (status && status !== "All") params.status = status;
  if (includeInsightLoss) params.include_insight_loss = true;
  // rank_by=loss forces the pass server-side even without the flag, so the
  // timeout is widened for it too.
  const slow = includeInsightLoss || rankBy === "loss";
  const { data } = await api.get(`${BASE}/performance`, {
    params,
    ...(slow ? { timeout: DETECTOR_TIMEOUT_MS } : {}),
  });
  return data;
}

/**
 * Portfolio-wide totals, ROI, payback and the cumulative benefit curve.
 *
 * Returns CO2 and insight-loss totals as unavailable rather than zero — check
 * `.available` on each before rendering a number.
 *
 * Insight loss is opt-in via includeInsightLoss and deliberately OFF here by
 * default: it costs one detector pass per category per window per initiative, and
 * this is the endpoint the dashboard loads on open. Left off it returns in a few
 * seconds; switched on it can take the better part of a minute and may only
 * measure some of the portfolio, which is why the response carries
 * measured_initiatives / initiatives_considered / timed_out_initiatives.
 */
export async function getOverallImpact(username, status, { includeInsightLoss = false } = {}) {
  const params = { username };
  if (status && status !== "All") params.status = status;
  if (includeInsightLoss) params.include_insight_loss = true;
  const { data } = await api.get(`${BASE}/overall-impact`, {
    params,
    ...(includeInsightLoss ? { timeout: DETECTOR_TIMEOUT_MS } : {}),
  });
  return data;
}
